/**
 * Agent Builder cockpit API client (Phase 2, T2.4).
 *
 * Typed client for the additive backend routes (mounted only when the backend
 * `enable_agent_builder` flag is on):
 *   - GET/POST  /api/agents, /api/guardrails               (catalog, T2.1)
 *   - GET/POST/PUT /api/agent-workflows, .../validate, .../publish, .../versions (T2.2)
 *   - POST /api/agent-runs, GET /api/agent-runs/{id}, .../events (SSE), .../hitl (T2.3)
 *
 * Uses the shared apiClient (baseURL already ends in `/api`), so paths drop the
 * `/api` prefix — except the SSE stream, which is opened with a native EventSource
 * against the absolute URL. The backend identifies the user via X-User-Email.
 */

import apiClient from '../api';
import type {
  AgentDef,
  AgentRunRecord,
  AgentRunResult,
  AgentWorkflow,
  AgentWorkflowVersion,
  AsyncRunStart,
  GuardrailDef,
  HitlDecisionRequest,
  RunEventFrame,
  RunListItem,
  StartRunRequest,
  ValidationResult,
} from '../../types/agentBuilder';

function userHeaders(userEmail?: string): Record<string, string> {
  const email = userEmail || localStorage.getItem('user_email') || '';
  return email ? { 'X-User-Email': email } : {};
}

// ---------------------------------------------------------------------------
// Catalog (T2.1)
// ---------------------------------------------------------------------------

export const listAgents = async (): Promise<AgentDef[]> =>
  (await apiClient.get<AgentDef[]>('/agents')).data;

export const upsertAgent = async (agent: AgentDef, userEmail?: string): Promise<AgentDef> =>
  (await apiClient.post<AgentDef>('/agents', agent, { headers: userHeaders(userEmail) })).data;

export const listGuardrails = async (): Promise<GuardrailDef[]> =>
  (await apiClient.get<GuardrailDef[]>('/guardrails')).data;

export const upsertGuardrail = async (g: GuardrailDef, userEmail?: string): Promise<GuardrailDef> =>
  (await apiClient.post<GuardrailDef>('/guardrails', g, { headers: userHeaders(userEmail) })).data;

// ---------------------------------------------------------------------------
// Workflows (T2.2)
// ---------------------------------------------------------------------------

export const listWorkflows = async (): Promise<AgentWorkflow[]> =>
  (await apiClient.get<AgentWorkflow[]>('/agent-workflows')).data;

export const getWorkflow = async (id: string): Promise<AgentWorkflow> =>
  (await apiClient.get<AgentWorkflow>(`/agent-workflows/${encodeURIComponent(id)}`)).data;

export const createWorkflow = async (wf: AgentWorkflow, userEmail?: string): Promise<AgentWorkflow> =>
  (await apiClient.post<AgentWorkflow>('/agent-workflows', wf, { headers: userHeaders(userEmail) })).data;

export const updateWorkflow = async (id: string, wf: AgentWorkflow, userEmail?: string): Promise<AgentWorkflow> =>
  (await apiClient.put<AgentWorkflow>(`/agent-workflows/${encodeURIComponent(id)}`, wf, { headers: userHeaders(userEmail) })).data;

export const validateWorkflow = async (id: string): Promise<ValidationResult> =>
  (await apiClient.post<ValidationResult>(`/agent-workflows/${encodeURIComponent(id)}/validate`)).data;

export const publishWorkflow = async (id: string, userEmail?: string): Promise<AgentWorkflowVersion> =>
  (await apiClient.post<AgentWorkflowVersion>(`/agent-workflows/${encodeURIComponent(id)}/publish`, undefined, { headers: userHeaders(userEmail) })).data;

export const listVersions = async (id: string): Promise<AgentWorkflowVersion[]> =>
  (await apiClient.get<AgentWorkflowVersion[]>(`/agent-workflows/${encodeURIComponent(id)}/versions`)).data;

/** Permanently delete a workflow + its published versions (run history is kept). */
export const deleteWorkflow = async (id: string, userEmail?: string): Promise<void> => {
  await apiClient.delete(`/agent-workflows/${encodeURIComponent(id)}`, { headers: userHeaders(userEmail) });
};

// ---------------------------------------------------------------------------
// Runs (T2.3)
// ---------------------------------------------------------------------------

export const startRun = async (req: StartRunRequest, userEmail?: string): Promise<AgentRunResult> =>
  (await apiClient.post<AgentRunResult>('/agent-runs', req, { headers: userHeaders(userEmail), timeout: 300000 })).data;

/**
 * Kick off a run on a background task and return {run_id, status:"running"}
 * immediately (subscribe-first live flow). The caller then opens the SSE stream to
 * animate node-by-node. Falls back to {@link startRun} (synchronous) if this
 * endpoint isn't available (older backend).
 */
export const startRunAsync = async (req: StartRunRequest, userEmail?: string): Promise<AsyncRunStart> =>
  (await apiClient.post<AsyncRunStart>('/agent-runs/stream', req, { headers: userHeaders(userEmail), timeout: 60000 })).data;

/** List recent runs (newest first), optionally filtered by workflow / case. */
export const listRuns = async (
  params: { workflowId?: string; caseId?: string; limit?: number } = {}
): Promise<RunListItem[]> => {
  const res = await apiClient.get<{ runs: RunListItem[] }>('/agent-runs', { params });
  return res.data?.runs ?? [];
};

export const getRun = async (runId: string): Promise<AgentRunRecord> =>
  (await apiClient.get<AgentRunRecord>(`/agent-runs/${encodeURIComponent(runId)}`)).data;

export const decideHitl = async (
  runId: string,
  decision: HitlDecisionRequest,
  userEmail?: string
): Promise<AgentRunResult> =>
  (await apiClient.post<AgentRunResult>(`/agent-runs/${encodeURIComponent(runId)}/hitl`, decision, { headers: userHeaders(userEmail), timeout: 300000 })).data;

/**
 * Subscribe to a run's SSE event stream. Returns an unsubscribe function.
 * Mirrors the ai_assistant_stream SSE contract: each frame is `event: <type>` +
 * `data: <json>`. The backend replays the run's hash-chained audit events.
 */
export function subscribeRunEvents(
  runId: string,
  onEvent: (frame: RunEventFrame) => void,
  onDone?: (summary: RunEventFrame) => void,
  onError?: (err: unknown) => void
): () => void {
  const base = (apiClient.defaults.baseURL || '').replace(/\/$/, '');
  const url = `${base}/agent-runs/${encodeURIComponent(runId)}/events`;
  const es = new EventSource(url);

  const RUN_EVENT_TYPES = [
    'run_started',
    'node_status',
    'agent_result',
    'awaiting_hitl',
    'hitl_decision',
    'run_finished',
  ];

  for (const t of RUN_EVENT_TYPES) {
    es.addEventListener(t, (e: MessageEvent) => {
      try {
        onEvent({ type: t, ...JSON.parse(e.data) });
      } catch {
        /* ignore malformed frame */
      }
    });
  }
  es.addEventListener('done', (e: MessageEvent) => {
    try {
      onDone?.({ type: 'done', ...JSON.parse(e.data) });
    } catch {
      onDone?.({ type: 'done' });
    }
    es.close();
  });
  es.addEventListener('error', (e: MessageEvent) => {
    onError?.(e);
    es.close();
  });

  return () => es.close();
}

export const agentBuilderApi = {
  listAgents,
  upsertAgent,
  listGuardrails,
  upsertGuardrail,
  listWorkflows,
  getWorkflow,
  createWorkflow,
  updateWorkflow,
  validateWorkflow,
  publishWorkflow,
  listVersions,
  deleteWorkflow,
  startRun,
  startRunAsync,
  listRuns,
  getRun,
  decideHitl,
  subscribeRunEvents,
};

export default agentBuilderApi;
